import * as THREE from 'three';
import { ArrayBufferTarget, Muxer } from 'mp4-muxer';

import { pickVideoConfig } from '../video-editor/export';

/**
 * A short film of the real tiles, rendered by the 3D studio itself.
 *
 * Product-on-white motion — a tile turning, the camera gliding along an edge,
 * light sweeping across the print — is exactly what a 3D render does
 * perfectly and a video model only approximates: the print, the thickness and
 * the joints are the product's own in every frame. So these clips are not
 * generated at all. Each frame is posed, drawn by the studio (its lights, its
 * depth of field), copied into an even-sized frame and encoded to H.264 in the
 * browser, the way the film editor exports.
 */

export const MOVES_3D = ['static', 'orbit', 'push-in', 'pull-out', 'pan', 'tilt', 'glide', 'spin', 'light-sweep'] as const;
export type Move3d = typeof MOVES_3D[number];

export interface RecordClipInput {
    move: Move3d;
    seconds: number;
    fps?: number;
    /** Output size; the 3D canvas is scaled into it. */
    width: number;
    height: number;
    canvas: HTMLCanvasElement;
    /** For the light sweep's exposure. */
    renderer?: THREE.WebGLRenderer;
    camera: THREE.PerspectiveCamera;
    controls: any;
    scene: THREE.Scene;
    pieces: THREE.Object3D[];
    /** Resolves once the studio has drawn a new frame of the current pose. */
    nextFrame: () => Promise<void>;
    signal?: { cancelled: boolean };
}

const smooth = (u: number) => u * u * u * (u * (u * 6 - 15) + 10);

/** The brightest directional light — the key the light sweep moves. */
function keyLight(scene: THREE.Scene): THREE.DirectionalLight | null {
    let best: THREE.DirectionalLight | null = null;
    scene.traverse((o: any) => {
        if (o.isDirectionalLight && (!best || o.intensity > best.intensity)) best = o;
    });
    return best;
}

/** Pose the scene at u (0..1) of the move. Returns a function that restores it. */
function poser(input: RecordClipInput) {
    const { camera, controls, pieces, scene, move } = input;
    const p0 = camera.position.clone();
    const t0 = (controls?.target as THREE.Vector3 | undefined)?.clone() || new THREE.Vector3();
    const offset = p0.clone().sub(t0);
    const dist = offset.length();
    const forward = t0.clone().sub(p0).normalize();
    const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
    if (right.lengthSq() < 1e-6) right.set(1, 0, 0); // looking straight down
    const piece0 = pieces.map((o) => ({ o, pos: o.position.clone(), rot: o.rotation.clone() }));
    const centre = pieces.reduce((c, o) => c.add(o.position), new THREE.Vector3()).multiplyScalar(1 / Math.max(1, pieces.length));
    const light = keyLight(scene);
    const light0 = light ? { pos: light.position.clone(), intensity: light.intensity } : null;
    const exposure0 = input.renderer?.toneMappingExposure ?? 1;

    const setCamera = (pos: THREE.Vector3, target: THREE.Vector3) => {
        camera.position.copy(pos);
        if (controls?.target) controls.target.copy(target);
        camera.lookAt(target);
        controls?.update?.();
        camera.updateMatrixWorld(true);
    };

    const pose = (raw: number) => {
        const u = smooth(Math.min(1, Math.max(0, raw)));
        switch (move) {
            case 'orbit': {
                const a = (u - 0.5) * 0.7;
                const o = offset.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), a);
                setCamera(t0.clone().add(o), t0);
                break;
            }
            case 'push-in': setCamera(t0.clone().add(offset.clone().multiplyScalar(1 - 0.32 * u)), t0); break;
            case 'pull-out': setCamera(t0.clone().add(offset.clone().multiplyScalar(0.68 + 0.32 * u)), t0); break;
            case 'pan':
            case 'glide': {
                // Travel is measured against what the frame shows across: a
                // portrait frame sees a narrow strip, and a fixed ±0.3 × distance
                // swept a tile panel out of a 9:16 shot for most of its length
                // (13 Oct 2026). A glide moves the aim half as far as the camera,
                // so the pieces drift across with parallax and stay in frame.
                const fov = (camera as THREE.PerspectiveCamera).fov ?? 40;
                const halfWidth = dist * Math.tan(THREE.MathUtils.degToRad(fov / 2)) * ((camera as THREE.PerspectiveCamera).aspect || 1);
                const span = Math.min(dist * (move === 'glide' ? 0.6 : 0.45), halfWidth * (move === 'glide' ? 0.9 : 0.7));
                const shift = right.clone().multiplyScalar((u - 0.5) * span);
                setCamera(p0.clone().add(shift), t0.clone().add(move === 'glide' ? shift.clone().multiplyScalar(0.5) : shift));
                break;
            }
            case 'tilt': {
                const pos = p0.clone();
                pos.y += (0.5 - u) * 0.45 * dist;
                setCamera(pos, t0);
                break;
            }
            case 'spin': {
                // The pieces turn on the spot, about their common centre — the
                // camera stands back by the diagonal so no corner leaves the frame.
                const a = u * 0.9;
                for (const { o, pos, rot } of piece0) {
                    const rel = pos.clone().sub(centre).applyAxisAngle(new THREE.Vector3(0, 1, 0), a);
                    o.position.copy(centre.clone().add(rel));
                    o.rotation.set(rot.x, rot.y, rot.z);
                    o.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), a);
                    o.updateMatrixWorld(true);
                }
                setCamera(t0.clone().add(offset.clone().multiplyScalar(1.38)), t0);
                break;
            }
            case 'light-sweep': {
                if (light && light0) {
                    const rel = light0.pos.clone().sub(t0).applyAxisAngle(new THREE.Vector3(0, 1, 0), (u - 0.5) * 2.2);
                    light.position.copy(t0.clone().add(rel));
                    light.intensity = light0.intensity * (1 - 0.55 * u);
                }
                // The studio's soft environment light swamps one moving light;
                // the scene itself has to darken for the sweep to read.
                if (input.renderer) input.renderer.toneMappingExposure = exposure0 * (1.08 - 0.5 * u);
                setCamera(p0, t0);
                break;
            }
            case 'static':
            default:
                // Never quite still: a 3% drift keeps it from reading as a photo.
                setCamera(t0.clone().add(offset.clone().multiplyScalar(1 - 0.03 * u)), t0);
        }
    };

    const restore = () => {
        setCamera(p0, t0);
        for (const { o, pos, rot } of piece0) { o.position.copy(pos); o.rotation.copy(rot); o.updateMatrixWorld(true); }
        if (light && light0) { light.position.copy(light0.pos); light.intensity = light0.intensity; }
        if (input.renderer) input.renderer.toneMappingExposure = exposure0;
    };
    return { pose, restore };
}

/** Render the move frame by frame and encode it. The poster is the first frame. */
export async function recordClip(input: RecordClipInput): Promise<{ file: File; poster: Blob; width: number; height: number; seconds: number }> {
    const fps = input.fps || 30;
    const width = input.width - (input.width % 2);
    const height = input.height - (input.height % 2);
    const frames = Math.max(2, Math.round(input.seconds * fps));
    const config = await pickVideoConfig(width, height, fps, 12_000_000);
    if (!config) throw new Error('This browser cannot encode H.264 video');

    const target = new ArrayBufferTarget();
    const muxer = new Muxer({ target, video: { codec: 'avc', width, height, frameRate: fps }, fastStart: 'in-memory', firstTimestampBehavior: 'offset' } as any);
    let encodeError: any = null;
    const VE = (globalThis as any).VideoEncoder;
    const VF = (globalThis as any).VideoFrame;
    const encoder = new VE({ output: (chunk: any, meta: any) => muxer.addVideoChunk(chunk, meta), error: (e: any) => { encodeError = e; } });
    encoder.configure(config);

    // Every frame is drawn into one even-sized canvas: the encoder wants a
    // fixed, even size, and the studio's canvas is whatever the dialog is.
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const ctx = out.getContext('2d')!;
    const draw = () => {
        const s = input.canvas;
        // Cover: crop the 3D canvas to the output's shape, centred.
        const sr = s.width / s.height;
        const or = width / height;
        const sw = sr > or ? s.height * or : s.width;
        const sh = sr > or ? s.height : s.width / or;
        ctx.drawImage(s, (s.width - sw) / 2, (s.height - sh) / 2, sw, sh, 0, 0, width, height);
    };

    const { pose, restore } = poser(input);
    const controlsWereEnabled = input.controls?.enabled;
    const damping = input.controls?.enableDamping;
    if (input.controls) { input.controls.enabled = false; input.controls.enableDamping = false; }
    let poster: Blob | null = null;
    try {
        for (let f = 0; f < frames; f++) {
            if (input.signal?.cancelled) throw new Error('Cancelled');
            if (encodeError) throw encodeError;
            pose(f / (frames - 1));
            // eslint-disable-next-line no-await-in-loop
            await input.nextFrame();
            draw();
            if (f === 0) {
                // eslint-disable-next-line no-await-in-loop
                poster = await new Promise<Blob | null>((r) => out.toBlob((b) => r(b), 'image/jpeg', 0.92));
            }
            const frame = new VF(out, { timestamp: Math.round((f * 1e6) / fps), duration: Math.round(1e6 / fps) });
            encoder.encode(frame, { keyFrame: f % (fps * 2) === 0 });
            frame.close();
            // Let the encoder keep up instead of queueing the whole clip.
            while (encoder.encodeQueueSize > 6) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 5));
            }
        }
        await encoder.flush();
        if (encodeError) throw encodeError;
        muxer.finalize();
    } finally {
        restore();
        if (input.controls) { input.controls.enabled = controlsWereEnabled ?? true; input.controls.enableDamping = damping; }
        try { encoder.close(); } catch { /* already closed */ }
    }
    if (!poster) throw new Error('The clip has no first frame');
    const file = new File([target.buffer], `surface3d-clip-${Date.now()}.mp4`, { type: 'video/mp4' });
    return { file, poster, width, height, seconds: frames / fps };
}
